"use client";

import { Play, Star } from "lucide-react";
import Image from "next/image";

import { cn } from "@/lib/utils";

import { PhoneFrame } from "./phone-frame";

interface FakeFunnelOutputProps {
  className?: string;
}

export function FakeFunnelOutput({ className }: FakeFunnelOutputProps) {
  return (
    <PhoneFrame className={cn("w-[280px] sm:w-[300px]", className)}>
      <div className="flex h-[580px] flex-col bg-white">
        {/* hero — top padding accounts for the phone notch */}
        <div className="px-5 pb-4 pt-7">
          <div className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-amber-700">
            <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
            New cohort · spots open
          </div>
          <h3 className="mt-3 font-heading text-[19px] font-bold leading-[1.1] tracking-tight text-slate-900">
            The 12-week reset for men who hate the gym.
          </h3>
          <p className="mt-2 text-[10px] leading-snug text-slate-500">
            Lose 20 lbs and build real strength — without 5 AM workouts,
            crash diets, or supplement-bro nonsense.
          </p>
        </div>

        {/* fake video */}
        <div className="px-5">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-200 ring-1 ring-slate-900/10">
            <Image
              src="/funnel-demo-thumb.jpg"
              alt=""
              fill
              sizes="280px"
              className="object-cover"
              aria-hidden
            />
            {/* dim overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/40"
            />
            {/* play button */}
            <div
              aria-hidden
              className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_6px_20px_-6px_rgba(15,23,42,0.6)] ring-1 ring-white/40 backdrop-blur"
            >
              <Play className="ml-0.5 h-4 w-4 fill-slate-900 text-slate-900" />
            </div>
            {/* duration tag */}
            <div className="absolute bottom-1.5 right-1.5 rounded bg-slate-900/85 px-1 py-0.5 text-[7.5px] font-semibold tracking-wide text-white">
              8:42
            </div>
          </div>
        </div>

        {/* social proof under video */}
        <div className="flex items-center justify-center gap-2 px-5 py-3">
          <div className="flex -space-x-1.5">
            {["#A78BFA", "#7C3AED", "#5B21B6", "#F97316"].map((c) => (
              <div
                key={c}
                className="h-5 w-5 rounded-full border-2 border-white"
                style={{ background: c }}
              />
            ))}
          </div>
          <p className="text-[9.5px] font-medium text-slate-500">
            <span className="font-semibold text-slate-900">2,400+</span> joined
            this month
          </p>
        </div>

        {/* lead form pinned to bottom */}
        <div className="mt-auto border-t border-slate-100 bg-slate-50/70 px-5 pb-5 pt-4">
          <p className="text-[10.5px] font-semibold text-slate-900">
            Get your free starter plan
          </p>
          <p className="mt-0.5 text-[8px] text-slate-500">
            Sent to your inbox in 30 seconds.
          </p>
          <div className="mt-2.5 flex h-7 items-center rounded-md bg-white px-2.5 text-[9px] text-slate-400 ring-1 ring-slate-200">
            you@email.com
          </div>
          <div
            className="mt-1.5 flex h-7 items-center justify-center rounded-md text-[9.5px] font-semibold text-white shadow-sm"
            style={{
              background:
                "linear-gradient(135deg, #1463F0 0%, #6B3BCE 38%, #EC4938 72%, #F29120 100%)",
            }}
          >
            Send my plan →
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
